import React, { useContext, useState } from "react";
import classnames from "classnames";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useUserEmail from "@/shared/hooks/useUserEmail";

import { Portal } from "@/App/components/Portal/Portal";
import { LoginRoute, LogoutRoute } from "@/routes/auth";
import { getUserId, setAfterLoginRedirectURL } from "../../../../api/localStorage";
import { hideNavigationRoutes, leftNavigationItems, getRightNavigationItems, menuItemI } from "./config";
import { scrollToPosition } from "@/shared/utils/mobileUtils";

import Styles from "./NavigationMobile.module.scss";
import AppContext from "@/App/context/AppContext";
import Icon from "../../../../shared/components/Icon/Icon";
import { useTheme } from "@/shared/styles/useTheme";
import { useFlavour } from "@/shared/flavours/useFlavour";
import ProfileButton from "../ProfileButton";
import { useQuery } from "react-query";
import { isAllowedRAFRoute } from "@/shared/utils/utils";
import { checkReferralEligibility } from "@/api/referral";

const hideLeftNavigation = hideNavigationRoutes.some((route) => location.pathname.includes(route));

const Breadcrums = ({
  openIndex,
  setOpenIndex,
  rightNavigationItems,
}: {
  openIndex: number;
  setOpenIndex: (index: number) => void;
  rightNavigationItems: menuItemI[];
}) => {
  const combinedNavigationItems = [...leftNavigationItems, ...rightNavigationItems];
  return (
    <div className={Styles.breadcrumbs} onClick={() => setOpenIndex(-1)}>
      {!hideLeftNavigation && <div>Menü</div>}
      {openIndex > -1 && (
        <div className={Styles.breadcrumbLabel}>
          <Icon className={Styles.arrow} name="angleDown" />
          <span>{combinedNavigationItems[openIndex].label}</span>
        </div>
      )}
    </div>
  );
};

const MenuItems = ({ rightNavigationItems }: { rightNavigationItems: menuItemI[] }) => {
  const { features } = useFlavour();
  const { loggedIn } = useContext(AppContext);
  const [openIndex, setOpenIndex] = useState(-1);

  const combinedNavigationItems = hideLeftNavigation ? [] : [...leftNavigationItems, ...rightNavigationItems];
  const profileIndex = combinedNavigationItems.findIndex((item) => item.label === "profile");

  const isVisible = (hide?: string[]) => {
    if (!hide) return true;
    if (hide.includes("auth") && loggedIn) return false;
    if (hide.includes("anon") && !loggedIn) return false;
    if (hide.includes("ratgeber") && !features?.selectivePages.ratgeber) return false;
    if (hide.includes("topDeals") && !features?.selectivePages.topDeals) return false;
    if (hide.includes("reminders") && !features?.selectivePages.reminders) return false;
    if (hide.includes("freunde-werben-freunde")) {
      return isAllowedRAFRoute(location.pathname);
    }
    return true;
  };

  return (
    <div className={Styles.menu}>
      <Breadcrums openIndex={openIndex} setOpenIndex={setOpenIndex} rightNavigationItems={rightNavigationItems} />
      {openIndex < 0 &&
        combinedNavigationItems.map(
          (item, index) =>
            item.label !== "profile" &&
            isVisible(item.hide) && (
              <div className={Styles.menuItem} key={`menuItem_${index}`}>
                {!item.children ? (
                  <Link
                    // eslint-disable-next-line
                    to={item.url!}
                    onClick={() => {
                      setOpenIndex(-1);
                      scrollToPosition(0, 0);
                    }}
                  >
                    <span>{item.label}</span>
                  </Link>
                ) : (
                  <div className={Styles.label} onClick={() => setOpenIndex(index)}>
                    {item.label}
                    <Icon className={Styles.arrow} name="angleDown" />
                  </div>
                )}
              </div>
            )
        )}
      {openIndex > -1 &&
        combinedNavigationItems[openIndex].children?.map(
          (child, childIndex) =>
            isVisible(child.hide) && (
              <div className={Styles.menuItem} key={`subMenuItem_${childIndex}`}>
                {child.url && (
                  <Link to={child.url} onClick={() => scrollToPosition(0, 0)}>
                    {child.label}
                  </Link>
                )}
              </div>
            )
        )}
      {loggedIn && profileIndex >= 0 && (
        <div className={Styles.bottomItems}>
          {combinedNavigationItems[profileIndex].children?.map(
            (child, childIndex) =>
              isVisible(child.hide) && (
                <div className={Styles.menuItem} key={`subMenuItem_${childIndex}`}>
                  {child.url && (
                    <Link to={child.url} onClick={() => scrollToPosition(0, 0)}>
                      {child.label}
                    </Link>
                  )}
                </div>
              )
          )}
        </div>
      )}
    </div>
  );
};

type Props = {
  close: () => void;
};

const NavigationMobile = ({ close }: Props) => {
  const { primaryColor } = useTheme();
  const { loggedIn } = useContext(AppContext);
  const { t } = useTranslation();
  const [isHiding, setIsHiding] = useState(false);
  const userID = getUserId();
  const userEmail = useUserEmail();

  const { data: eligibilityData } = useQuery(["referral-eligibility", userID], () => checkReferralEligibility(userID), {
    enabled: !!userID,
  });
  const showReferral = eligibilityData?.showRef;

  const closeMenu = () => {
    setIsHiding(true);
    setTimeout(() => {
      close();
    }, 300);
  };

  const rightNavigationItems = getRightNavigationItems(showReferral, userEmail);

  return (
    <Portal>
      <div className={classnames(Styles.overlay, { [Styles.close]: isHiding })} />
      <div className={classnames(Styles.navigation, { [Styles.close]: isHiding })} id="mobileNavigation">
        <div className={Styles.header}>
          <div className={Styles.user}>
            <Icon name="accountCircle" size="37px" />
            <div className={Styles.userNameAndLogout}>
              <ProfileButton className={""} isMobile={true} />
              {loggedIn ? (
                <Link to={LogoutRoute} className={classnames(primaryColor, Styles.clickable)}>
                  {t("navbar.myProfile.logout")}
                </Link>
              ) : (
                <div className="loginHolder">
                  <Link
                    to={LoginRoute}
                    className={primaryColor}
                    onClick={() => {
                      const currentURL = new URL(window.location.href);
                      setAfterLoginRedirectURL(currentURL.pathname);
                    }}
                  >
                    {t("navbar.login")}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <Icon className={Styles.clickable} name="closeThick" size="28px" onClick={closeMenu} />
        </div>
        <MenuItems rightNavigationItems={rightNavigationItems} />
      </div>
    </Portal>
  );
};

export default NavigationMobile;
